import React from "react";
import { ShieldCheck, Info, Sparkle } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TONE = {
  Excellent: "bg-mv-primary text-white",
  Strong: "bg-mv-primary/15 text-mv-primary",
  Solid: "bg-mv-accent/30 text-mv-secondary",
  Developing: "bg-amber-100 text-amber-800",
  Caution: "bg-rose-100 text-rose-700",
  "New Seller": "bg-gray-100 text-gray-600",
};

const TIP =
  "Trust Score is calculated from delivery history, reviews, disputes, cancellations, response time, and repeat buyer activity. Higher is better.";

/**
 * <TrustScore score={92} label="Excellent" size="md" />
 * size: "sm" (chip)  | "md" (inline)  | "lg" (profile)
 * If score is null and isNew is true, renders a "New Seller" pill.
 */
export default function TrustScore({ score, label, isNew, size = "md", className = "", testId }) {
  const tone = TONE[label] || TONE.Solid;
  const showNew = isNew || score === null || score === undefined;

  if (size === "sm") {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid={testId || "trust-score-sm"}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tone} ${className}`}
            >
              <ShieldCheck size={11} weight="duotone" />
              {showNew ? "New seller" : `${score} · ${label}`}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px] text-xs">{TIP}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (size === "lg") {
    return (
      <div
        data-testid={testId || "trust-score-lg"}
        className={`bg-white rounded-xl border border-gray-200 p-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-xs uppercase font-bold tracking-wider text-gray-500">
          <ShieldCheck size={14} weight="duotone" /> Trust Score
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button aria-label="What is this?" className="ml-auto text-gray-400 hover:text-mv-primary">
                  <Info size={14} weight="duotone" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] text-xs">{TIP}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {showNew ? (
          <>
            <div className="font-display font-black text-2xl text-gray-500 mt-1 flex items-center gap-1">
              <Sparkle size={18} weight="duotone" className="text-mv-accent" /> New
            </div>
            <div className="text-xs text-gray-500 mt-1">Not enough data yet</div>
          </>
        ) : (
          <>
            <div className="font-display font-black text-3xl text-mv-secondary mt-1">{score}<span className="text-gray-400 text-lg">/100</span></div>
            <div className={`mt-1 inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>{label}</div>
          </>
        )}
      </div>
    );
  }

  // md (default)
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId || "trust-score-md"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${tone} ${className}`}
          >
            <ShieldCheck size={13} weight="duotone" />
            {showNew ? "New seller" : `Trust ${score} · ${label}`}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px] text-xs">{TIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
